import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {IApplicationState} from "../../../../store/Store";
import {IElementTypeDto} from "../../../../common/apis/Elements";
import {_transl} from "../../../../store/localization/TranslMessasge";
import ElementTypeSingleSelectField from "../elements/ElementTypeSingleSelectField";
import {StereotypesTranslationKey} from "./StereotypesTranslationKey";
import {SaveButton} from "../../../../components/button/SaveButton";
import {CancelButton} from "../../../../components/button/CancelButton";
import Dialog from "../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../components/dialogs/DialogTitle";
import DialogContent from "../../../../components/dialogs/DialogContent";
import TextField from "../../../../components/fields/textfield/TextField";
import DialogActions from "../../../../components/dialogs/DialogActions";
import Grid from "../../../../components/dialogs/Grid";
import {CommonTranslation} from "../CommonTranslation";
import Snackbar from "../snackbar/Snackbar";
import stereotypeService from "../../../../common/apis/stereotype/StereotypeApiClient";
import {StereotypeCreateDto} from "../../../../common/apis/stereotype/StereotypeDto";
import {ValidationError} from "../../../../common/ValidationError";
import {StereotypeErrorCode} from "./StereotypeErrorCodes";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {RadioGroup} from "../../../../components/fields/RadioGroup";

export enum IdentifierOption {
    AUTO = "AUTO",
    MANUAL = "MANUAL"
}

interface StereotypeCreateDialogProps {
    open: boolean,
    onClosed: () => void,
    onStereotypeAdded: () => void,
}

export default function StereotypeCreateDialog(props: StereotypeCreateDialogProps) {
    const {onClosed, open, onStereotypeAdded} = props;

    const [identifier, setIdentifier] = useState<string>("");
    const [identifierError, setIdentifierError] = useState<string | undefined>(undefined);
    const [identifierOption, setIdentifierOption] = useState<IdentifierOption>(IdentifierOption.AUTO);
    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string | undefined>(undefined);
    const [type, setType] = useState<IElementTypeDto | null>(null);
    const [typeError, setTypeError] = useState<string | undefined>(undefined);
    const [description, setDescription] = useState<string>("");
    const typeOptions = useSelector((state: IApplicationState) => state.pages.common.options.types.resource);

    useEffect(() => {
        if (!open) {
            setIdentifier("");
            setIdentifierError(undefined);
            setName("");
            setNameError(undefined);
            setType(null);
            setTypeError(undefined);
            setDescription("");
            setIdentifierOption(IdentifierOption.AUTO);
        }
    }, [open]);

    const validateIdentifier = (identifier: string): boolean => {
        let isIdentifierValid;

        if (identifier.trim().length === 0) {
            setIdentifierError(CommonTranslation.FILL_OUT_THIS_FIELD);
            isIdentifierValid = false;
        } else {
            setIdentifierError(undefined);
            isIdentifierValid = true;
        }

        return isIdentifierValid;
    }

    const validateName = (name: string): boolean => {
        let isNameValid;

        if (name.trim().length === 0) {
            setNameError(CommonTranslation.FILL_OUT_THIS_FIELD);
            isNameValid = false;
        } else {
            setNameError(undefined);
            isNameValid = true;
        }

        return isNameValid;
    }

    const validateType = (type: IElementTypeDto | null): boolean => {
        let isTypeValid;

        if (!type || type.name.trim().length === 0) {
            setTypeError(CommonTranslation.FILL_OUT_ITEM_FROM_LIST);
            isTypeValid = false;
        } else {
            setTypeError(undefined);
            isTypeValid = true;
        }

        return isTypeValid;
    }

    const isFormValid = () => {
        const isNameValid = validateName(name);
        const isTypeValid = validateType(type);
        const isIdentifierValid = identifierOption === IdentifierOption.MANUAL ? validateIdentifier(identifier) : true;

        return isNameValid && isTypeValid && isIdentifierValid;
    }

    function saveChanges() {
        if (isFormValid() && type) {
            const stereotypeCreate: StereotypeCreateDto = {
                elementType: type.name,
                identifier: identifier.trim() || undefined,
                name: name.trim(),
                description: description.trim() || undefined,
            };
            stereotypeService.createStereotype(stereotypeCreate)
                .then(() => {
                    onClosed();
                    onStereotypeAdded();
                    Snackbar.success(_transl(StereotypesTranslationKey.STEREOTYPE_CREATED_SUCCESSFULLY));
                })
                .catch((err) => {
                    if (err instanceof ValidationError && err.error.code === StereotypeErrorCode.CONFLICTING_NAME) {
                        setNameError(_transl(StereotypesTranslationKey.STEREOTYPE_WITH_SAME_NAME_ALREADY_EXISTS));
                    } else if (err instanceof ValidationError && err.error.code === StereotypeErrorCode.CONFLICTING_IDENTIFIER) {
                        setIdentifierError(_transl(StereotypesTranslationKey.STEREOTYPE_WITH_SAME_IDENTIFIER_ALREADY_EXISTS));
                    } else if (err instanceof ValidationError && err.error.code === StereotypeErrorCode.INVALID_IDENTIFIER_FORMAT) {
                        setIdentifierError(_transl(StereotypesTranslationKey.INVALID_IDENTIFIER_FORMAT));
                    } else {
                        Snackbar.error(_transl(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), err);
                    }
                });
        }
    }

    function handleOnOptionChanged(value: string) {
        setIdentifierOption(value as IdentifierOption);
    }

    return (
        <Dialog open={open} onClose={onClosed}>

            <DialogTitle id="scroll-dialog-title"
                         title={_transl(StereotypesTranslationKey.NEW_STEREOTYPE)}
                         onDialogClosed={onClosed}/>

            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <RadioGroup value={identifierOption}
                                    onChange={handleOnOptionChanged}
                                    options={[
                                        {
                                            value: IdentifierOption.AUTO,
                                            label: _transl(StereotypesTranslationKey.GENERATE_IDENTIFIER)
                                        },
                                        {
                                            value: IdentifierOption.MANUAL,
                                            label: _transl(StereotypesTranslationKey.CUSTOM_IDENTIFIER)
                                        }
                                    ]}/>
                    </Grid>
                    {identifierOption === IdentifierOption.MANUAL &&
                        <Grid item xs={12}>
                            <TextField id="identifier-field"
                                       label={_transl(StereotypesTranslationKey.IDENTIFIER)}
                                       value={identifier}
                                       errorMessage={identifierError && _transl(identifierError)}
                                       onChange={(identifier) => setIdentifier(identifier)}
                                       required={true}/>
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <TextField id="name-field"
                                   label={_transl(StereotypesTranslationKey.NAME)}
                                   value={name}
                                   errorMessage={nameError && _transl(nameError)}
                                   onChange={(name) => setName(name)}
                                   required={true}/>
                    </Grid>
                    <Grid item xs={12}>
                        <ElementTypeSingleSelectField
                            label={_transl(StereotypesTranslationKey.BASE_TYPE)}
                            value={type}
                            onChange={(type) => {
                                setType(type)
                            }}
                            options={typeOptions}
                            errorMessage={typeError && _transl(typeError)}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField id="description-field"
                                   label={_transl(CommonTranslation.DESCRIPTION)}
                                   value={description}
                                   onChange={(value) => setDescription(value)}
                                   multiline={true}
                                   rows={3}/>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <SaveButton onClick={saveChanges}/>
                <CancelButton onClick={onClosed}/>
            </DialogActions>
        </Dialog>
    );
}
